/**
 * Coordinates readout, Share, and Export KML.
 *
 * All three are small, and all three exist because the tab they belong to
 * used to open onto nothing at all.
 */

import { useState } from 'react';
import ToolPanelHeader, { ToolActions } from '../ToolPanel';
import { cfg, scopedLayers, type ToolContext } from './types';
import { CopyValue, EmptyHint, Field, LayerSelect, Notice, btnClass } from './shared';
import { formatScale, scaleAt } from '../../../labeling/ScaleVisibility';
import { getLayerDisplayName } from '../../../layers/capabilities';
import { downloadKml, featuresToKml } from '../../../layers/kmlExport';
import { loadFeaturesDetailed } from './featureLoad';

/* ─────────────────────── Coordinates readout ────────────────────────────── */

export function ReadoutPanel({ ctx }: { ctx: ToolContext }) {
  const decimals = Math.max(0, Math.min(8, cfg<number>(ctx.config, 'decimals', 5)));
  const showScale = cfg<boolean>(ctx.config, 'showScale', true);

  const centre = ctx.mapCenter;
  const point = ctx.pointer;

  /* The real cartographic denominator, corrected for latitude — the same
     number GeoServer and ArcGIS report, so "1:25,000" here means what it means
     in those tools. Zoom level is not a scale. */
  const scale = scaleAt(centre[0], ctx.mapZoom);

  return (
    <>
      <ToolPanelHeader
        title="Coordinates readout"
        description="Where the map is, and how close in you are."
        onClose={ctx.onClose}
      />

      <CopyValue
        label="Map centre"
        value={`${centre[0].toFixed(decimals)}, ${centre[1].toFixed(decimals)}`}
      />

      {point ? (
        <CopyValue
          label="Last clicked point"
          value={`${point.lat.toFixed(decimals)}, ${point.lng.toFixed(decimals)}`}
        />
      ) : (
        <EmptyHint>Click the map to pin a point here.</EmptyHint>
      )}

      {showScale && (
        <div className="grid grid-cols-2 gap-1.5">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Scale</p>
            <p className="font-mono text-[12px] tabular-nums text-slate-700">{formatScale(scale)}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Zoom</p>
            <p className="font-mono text-[12px] tabular-nums text-slate-700">{ctx.mapZoom}</p>
          </div>
        </div>
      )}

      <p className="text-[10.5px] leading-snug text-slate-400">
        Scale is computed at 96&nbsp;DPI and corrected for latitude, so it matches what ArcGIS and
        GeoServer report at the same view.
      </p>
    </>
  );
}

/* ─────────────────────────────── Share ──────────────────────────────────── */

export function SharePanel({ ctx }: { ctx: ToolContext }) {
  const includeView = cfg<boolean>(ctx.config, 'includeView', true);
  const [withView, setWithView] = useState(includeView);

  /* The published address is the builder's URL with a `viewer` parameter — the
     same link the Landing page hands out. Adding the current view makes the
     link land where the sender is looking, which is usually the point of
     sharing one. */
  const link = (() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('viewer', ctx.module.id);
      if (withView) {
        url.searchParams.set('at', `${ctx.mapCenter[0].toFixed(6)},${ctx.mapCenter[1].toFixed(6)}`);
        url.searchParams.set('z', String(ctx.mapZoom));
      } else {
        url.searchParams.delete('at');
        url.searchParams.delete('z');
      }
      return url.toString();
    } catch {
      return '';
    }
  })();

  return (
    <>
      <ToolPanelHeader
        title="Share"
        description="A link that opens this map read-only."
        onClose={ctx.onClose}
      />

      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          checked={withView}
          onChange={(event) => setWithView(event.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-blue-600"
        />
        <span className="min-w-0 flex-1">
          <span className="block text-[12px] font-medium text-slate-600">Open at this view</span>
          <span className="block text-[10.5px] leading-snug text-slate-400">
            Without this, the link opens at the map's saved home view.
          </span>
        </span>
      </label>

      <CopyValue label="Link" value={link} />

      <ToolActions>
        <button
          type="button"
          onClick={() => window.open(link, '_blank', 'noopener')}
          className={`${btnClass} gl-btn--secondary`}
        >
          Open in a new tab
        </button>
      </ToolActions>

      <Notice>
        Anyone opening this link still has to sign in, and still needs the map assigned to them. The
        link is an address, not a grant of access.
      </Notice>
    </>
  );
}

/* ────────────────────────────── Export KML ──────────────────────────────── */

export function ExportKmlPanel({ ctx }: { ctx: ToolContext }) {
  const allowed = scopedLayers(ctx.config, ctx.layers).filter(
    (layer) => layer.checked && !layer.isGroup,
  );
  const maxFeatures = cfg<number>(ctx.config, 'maxFeatures', 5000);

  const [layerId, setLayerId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const layer = allowed.find((entry) => entry.id === layerId);

  const runExport = async () => {
    if (!layer) return;
    setExporting(true);
    setError(null);
    setNotice(null);
    try {
      // `loadFeaturesDetailed` is the same fetch Select By Location and Query
      // already use — whichever service backs the layer, and the layer's own
      // filter honoured, so this can never export what the map is not showing.
      const { features, truncated } = await loadFeaturesDetailed(layer, ctx, { limit: maxFeatures });
      if (features.length === 0) {
        setError('This layer has no features to export right now — check its filter, or try another layer.');
        return;
      }
      const name = getLayerDisplayName(layer);
      downloadKml(name, featuresToKml(name, features));
      setNotice(
        truncated
          ? `Exported the first ${features.length.toLocaleString()} features — this layer has more ` +
              'than the configured limit.'
          : `Exported ${features.length.toLocaleString()} feature${features.length === 1 ? '' : 's'}.`,
      );
    } catch {
      setError('The export failed. Try again in a moment.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <ToolPanelHeader
        title="Export KML"
        description="Save a layer's features as a KML file, to open in Google Earth or another GIS."
        onClose={ctx.onClose}
      />

      {allowed.length === 0 ? (
        <EmptyHint>Switch on at least one layer in the Layers tab to export it.</EmptyHint>
      ) : (
        <>
          <Field label="Layer to export">
            <LayerSelect layers={allowed} value={layerId} onChange={setLayerId} />
          </Field>

          <ToolActions>
            <button
              type="button"
              disabled={!layer || exporting}
              onClick={() => void runExport()}
              className={`${btnClass} gl-btn--primary`}
            >
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </ToolActions>

          {error && <Notice tone="warn">{error}</Notice>}
          {notice && <Notice>{notice}</Notice>}

          <p className="text-[10.5px] leading-snug text-slate-400">
            Attributes travel with each feature as KML's own data fields, so they survive a reimport
            into another GIS — Google Earth shows them in the info balloon.
          </p>
        </>
      )}
    </>
  );
}
